<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";

const logger = useLogger("RankedReport");

/** 1) Expect 'id' from the parent */
const props = defineProps<{ id: string }>();

/** 2) Get access to the store's read method only */
const { getContentById } = useContentManager();

/**
 * 3) Read-only computed property wrapping the container definition data.
 */
const definitionData = computed(() => getContentById(props.id));


/** 6) Commands for context menu (optional) */
const commands = [
  {
    id: "editors-group",
    name: "Report",
    description: "Configure this report",
    type: "group",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Number of columns",
        action: async () => {
          logger.info("Edit command triggered for report", props.id);
          logger.debug("Emitting event for onEdit with report data", {
            data: definitionData.value,
          });
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 7) Expose commands & properties for the resource editor / context menu */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Development Areas",
    icon: "lucide:brain-circuit",
  },
});

const route = useRoute();
const { reportData, loading: storeLoading, fetchReports } = useReportsStore();
const report = inject<{ data: any[]; kind?: string } | null>("report", null);

watch(
  () => route.query.user,
  async (newId) => {
    if (report) return;
    logger.info("Fetching ranked users from API");
    await fetchReports(typeof newId === "string" ? newId : undefined);
  },
  { immediate: true }
);

const user = computed(() => {
  if (report && report.kind === "users" && Array.isArray(report.data)) {
    return report.data[0] ?? null;
  }

  return Array.isArray(reportData.value) ? reportData.value[0] ?? null : null;
});

const constructs = computed(() => {
  if (!user.value?.constructs) return [];
  return user.value.constructs.filter((c) => c.score > 0).map((construct: any) => ({
    name: construct.name.replace('&', 'and'),
    total: construct.score * 100,
    exceeds: construct.score >= 0.5 ? construct.score * 100 : 0,
    meets:
      construct.score >= 0.3 && construct.score < 0.5
        ? construct.score * 100
        : 0,
    doesNotMeet: construct.score < 0.3 ? construct.score * 100 : 0,
  }));
});

const constructsWithoutOverall = computed(() => {
  return constructs.value.filter((c) => !c.name.startsWith("OVERALL"));
});

const worstConstructs = computed(() => {
  return [...constructsWithoutOverall.value]
    .sort((a, b) => a.total - b.total)
    .slice(0, 5);
});

const xFormatter = (i: number): string =>
  `${worstConstructs.value.slice(0, 8)[i]?.name}`;

const CompetencyCategories = {
  doesNotMeet: {
    name: "Does Not Meet",
    color: "#fb2c36",
  },
  meets: {
    name: "Meets",
    color: "#fe9a00",
  },
  exceeds: {
    name: "Exceeds",
    color: "#00bc7d",
  },
};
</script>

<template>
  <div v-if="storeLoading && !report" class="text-center">
    <icon mode="svg"
      name="lucide:loader-circle"
      class="mx-auto text-5xl text-primary animate-spin"
    />
    <h3 class="mt-2 text-sm font-semibold text-foreground">
      We're getting things ready
    </h3>
    <p class="mt-1 text-sm text-muted-foreground">
      Please be patient as we're loading some data for you...
    </p>
  </div>
  <BarChart
    v-else
    :data="worstConstructs"
    :stacked="true"
    :categories="CompetencyCategories"
    :y-axis="['doesNotMeet', 'meets', 'exceeds']"
    :show-legend="false"
    :height="300"
    :x-formatter="xFormatter"
  />

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  editor: () => import("#bb/components/editor/reports/development-areas/index.vue"),
  componentId: ComponentDefinitionIds.DEVELOPMENT_AREAS_REPORT,
  icon: "lucide:brain-circuit",
  type: "Development Areas",
  description: "A detailed view of an individual's development areas.",
  default: {},
  categories: ["Reports"],
  order: 12004,
};
</script>
